import FoldedArrow from './folded-arrow.js'
import { mirrorPath as mirror } from 'silkpath'

export default class LeftInwardFoldedArrow extends FoldedArrow {

  reverseGradient = true

  handles = [{
    // Arrow head height/width
    defaults: [.15, .45],
    constraints: (dimensions, handles) => {
      return [
        this._constraints.x,
        this._constraints.y
      ]
    },
    cursor: 'all-scroll'
  }, {
    // inward length of the arrow
    defaults: [null, .75],
    constraints: (dimensions, handles) => {
      // The `x` value of this handle is dependent on the `x` value of the first handle, so we do not constrain it here.
      // The `y` constraint of this handle uses the stored `this._constraints.y` value, which is also the armpit constraints,
      // but we need to adjust for the tip height.

      const tipHeight = handles[1].coords[1] - handles[0].coords[1]
      const handle0MinY = this.handles[0].constraints(dimensions, handles)[1][0] // Can't go below the first handle's minY

      // y0 is strictly so the previous handle doesn't exceed its constraints based on this lower tip location
      const y0 = handle0MinY + tipHeight

      // y1 is the minimum y value that still produces a valid curve where the tip doesn't exceed the right bounds
      const y1 = this._constraints.y[0] + tipHeight

      return [
        null, [Math.max(y0, y1), 1]
      ]
    },
    cursor: 'ns-resize'
  }]

  getOutlinePath (curves, pointerConfig, intersection) {
    const outline = super.getOutlinePath(curves, pointerConfig, intersection)
    return mirror(outline, intersection.point[0])
  }

  getBackgroundPath (curves, pointerConfig, intersection) {
    const background = super.getBackgroundPath(curves, pointerConfig, intersection)
    return mirror(background, intersection.point[0])
  }

  getForegroundPath (curves, pointerConfig, intersection) {
    const foreground = super.getForegroundPath(curves, pointerConfig, intersection)
    return mirror(foreground, intersection.point[0])
  }

  getComponentCurveData (dimensions, arrowWidth, intersection0, intersection1, pointerY) {
    let data = super.getCurveData(dimensions, arrowWidth, intersection0, intersection1, pointerY)

    // The curves are shifted since this is a left facing arrow
    const offset = dimensions.width - data.right.outer.at(-1)[0]

    ;['left', 'right'].forEach(side => {
      ;['outer', 'inner'].forEach(curve => {
        data[side][curve] = data[side][curve].map(point => [point[0] + offset, point[1]])
      })
    })


    data.tipPoint = this.getPointerTip(data, pointerY) // Reset the tip point to the adjusted curve
    // Mirror the tippoint x value
    data.tipPoint[0] = dimensions.width - data.tipPoint[0] + offset
    return data
  }

  /*
   * Sets the internal constraints for the y value of the first handle (armpit of the arrow.)
   * This is dependent on the `x` value of the first handle handle.
   */
  _setYConstraints (dimensions, pointerHeight, handles) {
    const y = this.findConstraint(dimensions, pointerHeight, this._invertHandles(handles), 'y')
    return [y, handles[1][1]]
  }

  /*
   * Sets the internal constraints for the x value of the first handle (armpit of the arrow.)
   * This is dependent on the `y` value of the first handle handle.
   */
  _setXConstraints (dimensions, pointerHeight, handles) {
    const x = this.findConstraint(dimensions, pointerHeight, this._invertHandles(handles), 'x')
    return [1 - x, 0.18]
  }

  updateHandles (handles, dimensions) {
    handles[0][0] = (this.properties.arrowWidth / 2) / dimensions.width
    handles[1][1] = Math.min(handles[0][1] + this.properties.arrowHeight / dimensions.height, 1)

    // Set the x value of the 2nd handle to the tip of the arrow
    handles[1][0] = this.curveData.tipPoint[0] / dimensions.width
  }

  getFlattenedPaths (data, dimensions) {
    const handles = structuredClone(this.getHandles(dimensions).map(handle => handle.coords))
    handles[0][0] = .16
    handles[1][0] = .7

    const intersection0 = [0, dimensions.height]
    const intersection1 = [dimensions.width - (this.properties.arrowWidth + this.properties.arrowWidth / 2), dimensions.height * handles[0][1]]
    this.curveData = this.getComponentCurveData(dimensions, this.properties.arrowWidth, intersection0, intersection1, dimensions.height * handles[1][1])

    const paths = this.getPaths(data, dimensions, handles)
    paths.at(-1).fill = paths.at(0).fill
    paths.at(-1).stroke = paths.at(0).stroke
    paths.at(-1).strokeWidth = paths.at(0).strokeWidth
    return paths
  }

  getProperties (dimensions, handles) {
    return {
      arrowWidth: (dimensions.width * handles[0][0]) * 2,
      arrowHeight: dimensions.height * handles[1][1] - dimensions.height * handles[0][1]
    }
  }

  onHandleChange (handle, diff, handles, dimensions) {
    // Moving the tip, but the arrowhead height is maintained, so we need to adjust
    // the first handle to maintain the arrowhead height.
    if (handle.idx === 1) {
      handles[0].coords[1] += diff[1]
    }

    // Now we want to reset the constraints for the handles. It's more performant to do this
    // after all the handle changes have been made, rather than repeating calls in the `constraints` method.
    const pointerHeight = dimensions.height * handles[1].coords[1] - dimensions.height * handles[0].coords[1]

    if (handle.idx === 0 && diff[0]) {
      // Moving the armpit control and changed the x value, recalculate the y constraints
      this._constraints.y = this._setYConstraints(dimensions, pointerHeight, handles.map(h => h.coords))
    }

    if ((handle.idx == 0 && diff[1]) || (handle.idx == 1 && diff[1])) {
      // Changing the y value of the armpit control, either by sliding the tip or moving the armpit control directly
      this._constraints.x = this._setXConstraints(dimensions, pointerHeight, handles.map(h => h.coords))
    }

    // Reset the properties
    this.properties = this.getProperties(dimensions, handles.map(h => h.coords))
  }

  generateCurvePaths (data, dimensions, handles) {
    const tipBottom =  dimensions.height * handles[1][1] // This is at the tip of the arrow
    const pointerHeight = tipBottom - dimensions.height * handles[0][1]

    return this.getCurvePaths(dimensions, this.curveData, dimensions.height, pointerHeight, {
      height: tipBottom // pointer bounds
    })
  }

}
